import { useTranslation } from 'react-i18next'
import { CardCornerDebugInfo } from 'src/components/Admin/UserContentInfo/CardCornerDebugInfo'
import { getImageDownloadFileExt } from 'src/components/ArtworkDetail/utils'
import { ContentFittableBox } from 'src/components/Common/ContentFittableBox'
import MediaContentRemoved from 'src/components/Media/MediaContentRemoved'
import SingleImageViewer from 'src/components/Media/SingleImageViewer'
import { AppealButtonInDetail } from 'src/components/Report/AppealDialog'
import type {
  GenerationModelBaseFragment,
  GenerationModelDetailFragment,
  GenerationModelVersionDetailFragment,
} from 'src/services/apollo/generated/graphql'
import { RefType } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { getAspectRatio, getPublicUrl, getThumbnailUrl } from 'src/utils/media'
import { isModelCoverAppealable, isModelCoverWarned } from 'src/utils/model'
import { getModelCoverAlt } from 'src/utils/seo'
import { isModelPrivate } from '~/utils/generationModel/modelPermissions'
import { TrainingTaskStatus } from '../ModelTypeChip'
import { PublishOrEditUserLoraButton } from './ModelUserLoraAction'

export default function ModelDetailImage(
  props: StyleProps & {
    model: GenerationModelDetailFragment
    version?: GenerationModelVersionDetailFragment
    isSinglePage?: boolean
    mutate?: () => void
  },
) {
  const { t } = useTranslation(['market'])

  const { model, version, mutate } = props

  const media = version?.media ?? model.media
  const coverWarned = isModelCoverWarned(model)
  const isAppealable = isModelCoverAppealable(model)

  return (
    <ContentFittableBox
      ratio={getAspectRatio(media)}
      className={cx('size-full drop-shadow-md', props.className)}
      style={props.style}
    >
      {coverWarned ? (
        <div className="grid place-content-center place-items-center size-full">
          <MediaContentRemoved
            overlayType={coverWarned ? 'model-cover-warned' : undefined}
            type="standalone"
          />
          {isAppealable && (
            <AppealButtonInDetail
              className="mt-4"
              refType={RefType.Model}
              refId={model.id}
            />
          )}
        </div>
      ) : (
        // Render the cover via `<SingleImageViewer>` so clicking it
        // opens an in-page Photoswipe lightbox (matching production),
        // instead of navigating the browser to the raw CDN URL in a
        // new tab — which some browsers/CDN responses downgrade to a
        // forced "download" of the webp/jpg.
        //
        // The track-0a SSR branch had previously downgraded this
        // wrapper to a plain `<a href target="_blank">` when moving
        // `ContentFittableBox` into `src/components/Common/*`, which
        // dropped the `<SingleImageViewer>` integration by accident.
        <SingleImageViewer
          scene="model-detail"
          imgClassName="rounded-md"
          id={media?.id}
          mediaId={media?.id}
          fileName={`pixai-model-cover_${model.id}.${getImageDownloadFileExt({
            media,
          })}`}
          originalUrl={getPublicUrl(media)}
          thumbnailUrl={getThumbnailUrl(media)}
          width={media?.width}
          height={media?.height}
          author={model.author}
          alt={getModelCoverAlt(t, model)}
          // Detail cover is the LCP candidate; pair this with the
          // `<link rel="preload" as="image">` injected from
          // `_app.model.$modelIdSlug.($versionId).tsx` route meta.
          priority
        />
      )}
      <ModelOverlayForUserLora model={model} mutate={mutate} />
      <CardCornerDebugInfo
        extra={model.extra}
        artworkSafetyScore={model.artworkSafetyScore}
      >
        {content => (
          <div className="absolute top-1.5 right-1.5 bg-black/50 rounded px-2 py-1.5">
            {content}
          </div>
        )}
      </CardCornerDebugInfo>
    </ContentFittableBox>
  )
}

function ModelOverlayForUserLora(props: {
  model: GenerationModelDetailFragment
  mutate?: () => void
}) {
  const { model, mutate } = props
  const { trainingTask } = model

  if (!trainingTask || !isModelPrivate(model)) return null
  if (trainingTask.status === TrainingTaskStatus.completed)
    return (
      <div className="absolute text-white w-full h-full top-0 left-0 bg-black/20 backdrop-blur-sm items-center justify-center flex flex-col">
        <PublishOrEditUserLoraButton
          model={props.model}
          className="text-black bg-white font-medium text-xl px-4"
          mutate={mutate}
        />
      </div>
    )
  return null
}
